import { useState, useEffect, useCallback } from 'react';
import { API_URL } from '../config/apiConfig';

const CACHE_PREFIX = 'coda_tutorial_seen_';

function readTutorialSeenCache(userId) {
  if (userId == null) return null;
  try {
    const raw = localStorage.getItem(`${CACHE_PREFIX}${userId}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : null;
  } catch (err) {
    console.error('Failed to read tutorial seen cache:', err);
    return null;
  }
}

function writeTutorialSeenCache(userId, seenMessages) {
  if (userId == null || !Array.isArray(seenMessages)) return;
  try {
    localStorage.setItem(`${CACHE_PREFIX}${userId}`, JSON.stringify(seenMessages));
  } catch (err) {
    console.error('Failed to write tutorial seen cache:', err);
  }
}

function resolveUserId(user) {
  if (user?.userId != null) return Number(user.userId);
  const ls = localStorage.getItem('userId');
  return ls != null ? Number(ls) : null;
}

/**
 * Loads persisted tutorial message keys from the server.
 * Tutorials must not show until status is loaded — an empty initial list means "unknown", not "unseen".
 * On fetch failure, cached keys from a prior successful load suppress replays; otherwise tutorials stay hidden.
 */
export function useTutorialStatus(user) {
  const userId = resolveUserId(user);
  const [seenMessages, setSeenMessages] = useState(() => readTutorialSeenCache(userId) ?? []);
  const [isLoaded, setIsLoaded] = useState(false);
  const [fetchFailed, setFetchFailed] = useState(false);
  const [isAlwaysShowUser, setIsAlwaysShowUser] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token || !user) {
      setIsLoaded(true);
      setFetchFailed(false);
      return undefined;
    }

    let cancelled = false;
    setIsLoaded(false);
    setFetchFailed(false);

    (async () => {
      try {
        const res = await fetch(`${API_URL}/tutorial/status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) {
          throw new Error(`tutorial/status ${res.status}`);
        }
        const data = await res.json();
        if (!Array.isArray(data.seenMessages)) {
          throw new Error('tutorial/status response missing seenMessages array');
        }
        if (cancelled) return;
        setSeenMessages(data.seenMessages);
        setIsAlwaysShowUser(Boolean(data.isAlwaysShowUser));
        writeTutorialSeenCache(userId, data.seenMessages);
      } catch (err) {
        console.error('Failed to fetch tutorial status:', err);
        if (!cancelled) {
          setFetchFailed(true);
          const cached = readTutorialSeenCache(userId);
          if (cached) {
            setSeenMessages(cached);
          }
        }
      } finally {
        if (!cancelled) setIsLoaded(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user, userId]);

  const markSeenLocally = useCallback(
    (messageKey) => {
      setSeenMessages((prev) => {
        if (prev.includes(messageKey)) return prev;
        const next = [...prev, messageKey];
        writeTutorialSeenCache(userId, next);
        return next;
      });
    },
    [userId]
  );

  const shouldShowTutorial = useCallback(
    (messageKey) => {
      if (!user || !isLoaded || fetchFailed) return false;
      return !seenMessages.includes(messageKey);
    },
    [user, isLoaded, fetchFailed, seenMessages]
  );

  return {
    seenMessages,
    isLoaded,
    fetchFailed,
    isAlwaysShowUser,
    shouldShowTutorial,
    markSeenLocally,
    setSeenMessages,
  };
}
